import React, {useEffect} from 'react';
import { toast } from 'react-toastify';

import {items} from '../../items';
import {custom} from '../../custom';
import {svg} from '../../assets/svg';
import {theme} from '../../constants';
import {components} from '../../components';
import {actions} from '../../store/actions';
import {hooks, RootState} from '../../hooks';
import {GiCheckMark} from 'react-icons/gi';
import { discountCodeInquery } from '../../services/handle_discount';

export const Order: React.FC = () => {
  const dispatch = hooks.useAppDispatch();
  const navigate = hooks.useAppNavigate();
  const [loading, setLoading] = React.useState(false);
  const cart = hooks.useAppSelector(state => state.cartSlice.list);
  const token = hooks.useAppSelector(state => state.auth.token);
  const [discountCode, setDiscountCode] = React.useState('');

  const inqueryDiscount = async () => {
    try {
      setLoading(true);
      const orderPayload = {
        items: cart.map(item => ({
          id: item.id,
          title:item.title,
          quantity: item.quantity,
        })),
        discount_code: discountCode,
      };
      await discountCodeInquery(orderPayload, token, dispatch, navigate);

    } catch (error: any) {
      // اگر از کتابخونه toast مثل react-toastify یا custom استفاده می‌کنی:
      // alert(error.message || 'مشکلی در ثبت سفارش پیش آمد');
      toast.error(error.message || 'خطا در ثبت سفارش');

    } finally {
      setLoading(false);
    }
  };

  const {subtotal, discount, list, total, discountAmount, payAmount, promoCode} =
    hooks.useAppSelector((state: RootState) => state.cartSlice);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const renderHeader = (): JSX.Element | null => {
    return (
      <components.Header
        title='سفارش دهید'
        burger={true}
        basket={true}
        line={true}
      />
    );
  };

  const renderProducts = (): JSX.Element | null => {
    if (list.length > 0) {
      return (
        <div
          style={{
            marginBottom: 30,
            borderBottom: `1px solid ${theme.colors.lavenderMist}`,
          }}
        >
          {list?.map((item, index, array) => {
            const isLast = index === array.length - 1;
            return <items.OrderItem key={index} item={item} isLast={isLast} />;
          })}
        </div>
      );
    }

    return null;
  };

  const renderAppliedText = (): JSX.Element | null => {
    if (promoCode?.trim()) {
      return (
        <div style={{marginBottom: 30, padding: '0 20px'}}>
          {/* <svg.PromocodeAppliedSvg /> */}
          <div
            style={{display: 'flex', alignItems: 'center', color: '#193364'}}
          >
            <span
              style={{
                fontSize: '17px',
                marginRight: '2px',
              }}
            >
              کد تخفیف {promoCode} اعمال شد
            </span>
            <GiCheckMark color='#51ba74' size={16} />
          </div>
        </div>
      );
    }

    return null;
  };

  const renderApplyPromocode = (): JSX.Element | null => {
    if (promoCode?.trim()) return null;

    return (
      <div
        style={{
          marginBottom: 60,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '0 20px',
        }}
      >
        <custom.InputField
          label='کد تخفیف'
          containerStyle={{width: 'calc(70% - 5px)'}}
          placeholder={'کد تخفیف را وارد کنید'}
          onChange={(value: string) => setDiscountCode(value)
          }
        />
        <components.Button
          containerStyle={{padding: 0, width: 'calc(30% - 5px)'}}
          title={loading ? 'در حال ارسال...' : 'اعمال'}
          onClick={inqueryDiscount}
          disabled={loading}
        />
      </div>
    );
  };

  const renderTotal = (): JSX.Element | null => {
    if (list.length > 0) {
      return (
        <div
          style={{
            marginBottom: 20,
            padding: '0 20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h5
              style={{
                margin: 0,
                ...theme.fonts.Mulish_600SemiBold,
                fontSize: 16,
                color: theme.colors.mainColor,
                lineHeight: 1.5,
              }}
            >
              مبلغ کل
            </h5>
            <span
              style={{
                margin: 0,
                ...theme.fonts.Mulish_600SemiBold,
                fontSize: 16,
                color: theme.colors.mainColor,
                lineHeight: 1.5,
              }}
            >
              {total.toLocaleString()} تومان
            </span>

          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 10,
            }}
          >
            <h5
              style={{
                margin: 0,
                ...theme.fonts.Mulish_600SemiBold,
                fontSize: 16,
                color: theme.colors.textColor,
                lineHeight: 1.5,
              }}
            >
              تخفیف کالاها
            </h5>
            <span
              style={{
                margin: 0,
                ...theme.fonts.Mulish_600SemiBold,
                fontSize: 16,
                color: theme.colors.coralRed,
                lineHeight: 1.5,
              }}
            >               
              {discountAmount.toLocaleString()} تومان
            </span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <h4
              style={{
                margin: 0,
                ...theme.fonts.Mulish_600SemiBold,
                fontSize: 18,
                color: theme.colors.mainColor,
                lineHeight: 1.5,
              }}
            >
              مبلغ کل پس از تخفیف
            </h4>
            <span
              style={{
                margin: 0,
                ...theme.fonts.Mulish_600SemiBold,
                fontSize: 18,
                color: theme.colors.mainColor,
                lineHeight: 1.5,
              }}
            >
              {payAmount.toLocaleString()} تومان
            </span>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderButton = (): JSX.Element | null => {
    return (
      <components.Button
        title='ثبت نهایی سفارش'
        colorScheme='light'
        onClick={() => {
          navigate('/Checkout');
        }}
        containerStyle={{padding: '0 20px'}}
      />
    );
  };

  const renderContent = (): JSX.Element | null => {
    return (
      <div style={{paddingTop: 20, paddingBottom: 64 + 20}}>
        {renderProducts()}
        {renderAppliedText()}
        {renderApplyPromocode()}
        {renderTotal()}
        {renderButton()}
      </div>
    );
  };

  const renderBottomTabBar = (): JSX.Element | null => {
    return <components.BottomTabBar />;
  };

  return (
    <>
      {renderHeader()}
      {renderContent()}
      {renderBottomTabBar()}
    </>
  );
};
